import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users as UsersIcon, ShieldCheck, UserX } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import { staffViewsApi } from "@/api/endpoints";

interface URow {
  id: string;
  email: string;
  display_name: string | null;
  is_staff: boolean;
  is_active: boolean;
  organisation_id: string | null;
  organisation_name: string | null;
  role_name: string | null;
}

export default function StaffUsersPage() {
  const [rows, setRows] = useState<URow[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    staffViewsApi.users().then((r) => { setRows(r); setLoading(false); });
  }, []);

  const staffCount = rows.filter((r) => r.is_staff).length;
  const inactive = rows.filter((r) => !r.is_active).length;

  const columns: Column<URow>[] = [
    {
      key: "email", header: "Email",
      render: (u) => <span className="font-medium">{u.email}</span>,
    },
    { key: "name", header: "Name", render: (u) => u.display_name ?? "—" },
    {
      key: "role", header: "Role",
      render: (u) => u.is_staff
        ? <span className="badge bg-brand-100 text-brand-800"><ShieldCheck size={12} /> Staff</span>
        : (u.role_name ?? "—"),
    },
    {
      key: "org", header: "Organisation",
      render: (u) => u.organisation_id
        ? <Link to={`/staff/organisations/${u.organisation_id}/users`} className="text-brand-700 hover:underline">{u.organisation_name}</Link>
        : <span className="text-slate-400">—</span>,
    },
    {
      key: "active", header: "Status",
      render: (u) => u.is_active
        ? <span className="badge bg-brand-50 text-brand-800">Active</span>
        : <span className="badge bg-slate-100 text-slate-600">Inactive</span>,
    },
  ];

  return (
    <AppLayout>
      <PageHeader title="Users across all organisations" subtitle="Including UmdoniWay staff" />
      {loading ? <LoadingState /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <KpiCard label="Total users" value={rows.length} icon={<UsersIcon size={18} />} />
            <KpiCard label="UmdoniWay staff" value={staffCount} icon={<ShieldCheck size={18} />} accent="brand" />
            <KpiCard label="Inactive users" value={inactive} icon={<UserX size={18} />} accent={inactive ? "amber" : "slate"} />
          </div>
          <DataTable columns={columns} rows={rows} emptyMessage="No users yet" />
        </>
      )}
    </AppLayout>
  );
}
